import {Button} from '@dropbox/dig-components/buttons';
import {Popover} from '@dropbox/dig-components/popover';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {
  ApiError,
  type ProductRoadmapEmbedConfigResponse,
  ProductRoadmapService,
  type Workstream,
} from 'client';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {getService} from 'utilities';
import {invalidateNavTrees} from 'views/workstreams/page/useNavTree';
import {logWorkstreamsInteraction} from 'views/workstreams/page/workstreamsAnalytics';

import {WorkstreamModalFieldError} from './WorkstreamModalField';
import {WorkstreamRoadmapChip} from './WorkstreamRoadmapChip';
import {WorkstreamRoadmapEmbed} from './WorkstreamRoadmapEmbed';
import {WorkstreamRoadmapMenu} from './WorkstreamRoadmapMenu';

type RoadmapWorkstream = Pick<Workstream, 'id'>;

// Roadmaps are linked without a name; the chip and the rail call them "Roadmap".
const ROADMAP_TITLE = 'Roadmap';

const productRoadmapQueryKey = (workstreamId: number) =>
  ['workstream', 'product-roadmap', workstreamId] as const;

// Any web link, as the server accepts (api/endpoints/product_roadmap.py).
const isWorkstreamProductRoadmapUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
};

const saveErrorMessage = (error: unknown) =>
  error instanceof ApiError && error.status < 500 && typeof error.body?.detail === 'string'
    ? error.body.detail
    : t('unable_to_save');

/**
 * Paste a roadmap link and save: adds one, or relinks `roadmap` when set. A hint
 * shows under the field; it turns into the error once Add has been tried with
 * something that isn't a web link, and stays live after that.
 */
const RoadmapLinkForm = ({
  workstream,
  roadmap,
  onDone,
}: {
  workstream: RoadmapWorkstream;
  roadmap: ProductRoadmapEmbedConfigResponse | null;
  onDone: () => void;
}) => {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(roadmap?.open_url ?? '');
  const [attempted, setAttempted] = useState(false);
  const {mutate, isPending, error, reset} = useMutation({
    mutationFn: (link: string) => {
      const document = {title: ROADMAP_TITLE, url: link};
      return roadmap?.id
        ? getService(ProductRoadmapService).editProductRoadmapApiV1ProductRoadmapRoadmapIdPatch(
            roadmap.id,
            document
          )
        : getService(ProductRoadmapService).createProductRoadmapApiV1ProductRoadmapPost({
            workstream_id: workstream.id,
            ...document,
          });
    },
    retry: false,
    onSuccess: () => {
      logWorkstreamsInteraction(roadmap ? 'roadmap_edited' : 'roadmap_added', {
        workstream_id: workstream.id,
      });
      invalidateNavTrees();
      return queryClient.invalidateQueries({
        queryKey: productRoadmapQueryKey(workstream.id),
        exact: true,
      });
    },
  });
  const isValid = isWorkstreamProductRoadmapUrl(url);
  const isInvalid = attempted && url.trim().length > 0 && !isValid;
  const message = isInvalid
    ? t('workstream_product_roadmap_invalid_link')
    : error
      ? saveErrorMessage(error)
      : null;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setAttempted(true);
        if (isValid && !isPending) mutate(url.trim(), {onSuccess: onDone});
      }}
    >
      <Stack gap="8" style={{width: 400}}>
        <Text size="small" isBold>
          {roadmap ? t('workstream_product_roadmap_edit') : t('workstream_product_roadmap_add')}
        </Text>
        <Box display="flex" alignItems="center" style={{gap: 8}}>
          <Box style={{flex: 1, minWidth: 0}}>
            <TextInput
              aria-label={t('workstream_product_roadmap_link')}
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                reset();
              }}
              placeholder="https://"
              autoFocus
              disabled={isPending}
              isInvalid={Boolean(message)}
              size="small"
            />
          </Box>
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={!url.trim() || isPending}
            isLoading={isPending}
          >
            {roadmap ? t('save') : t('add')}
          </Button>
        </Box>
        {message ? (
          <WorkstreamModalFieldError>{message}</WorkstreamModalFieldError>
        ) : (
          <Text size="small" color="faint">
            {t('workstream_product_roadmap_hint')}
          </Text>
        )}
      </Stack>
    </form>
  );
};

/**
 * The detail row's Roadmap chip. Without a roadmap, a click opens a field to paste
 * its link; with one, a menu to open it, change the link or remove it. A linked
 * roadmap also shows in the rail, after the row's narrative.
 */
export const WorkstreamProductRoadmapButton = ({
  workstream,
  embedSlot,
  onRoadmapContentVisibilityChange,
}: {
  workstream: RoadmapWorkstream;
  embedSlot?: HTMLElement | null;
  onRoadmapContentVisibilityChange?: (open: boolean) => void;
}) => {
  const {isWorkstreamProductRoadmapEnabled, isWorkstreamProductRoadmapIframeEnabled} =
    useAtomValue(growthbookCacheAtom);
  const queryClient = useQueryClient();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEmbedOpen, setIsEmbedOpen] = useState(false);
  const {data: roadmap, isError} = useQuery({
    queryKey: productRoadmapQueryKey(workstream.id),
    queryFn: async () => {
      try {
        return await getService(
          ProductRoadmapService
        ).getProductRoadmapEmbedConfigApiV1ProductRoadmapEmbedConfigGet(undefined, workstream.id);
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.status === 404 &&
          error.body?.detail === 'Product Roadmap document is not configured for workstream'
        ) {
          return null;
        }
        throw error;
      }
    },
    enabled: isWorkstreamProductRoadmapEnabled,
    retry: false,
  });
  const remove = useMutation({
    mutationFn: (roadmapId: number) =>
      getService(ProductRoadmapService).deleteProductRoadmapApiV1ProductRoadmapRoadmapIdDelete(
        roadmapId
      ),
    onSuccess: () => {
      logWorkstreamsInteraction('roadmap_removed', {workstream_id: workstream.id});
      setIsEmbedOpen(false);
      invalidateNavTrees();
      return queryClient.invalidateQueries({
        queryKey: productRoadmapQueryKey(workstream.id),
        exact: true,
      });
    },
  });

  const shouldShowRoadmapEmbed =
    isWorkstreamProductRoadmapEnabled &&
    isWorkstreamProductRoadmapIframeEnabled &&
    Boolean(roadmap) &&
    isEmbedOpen;
  useEffect(() => {
    onRoadmapContentVisibilityChange?.(shouldShowRoadmapEmbed);
    return () => onRoadmapContentVisibilityChange?.(false);
  }, [onRoadmapContentVisibilityChange, shouldShowRoadmapEmbed]);

  if (!isWorkstreamProductRoadmapEnabled) return null;

  const openRoadmap = () => {
    if (!roadmap) return;
    logWorkstreamsInteraction('roadmap_link_opened', {workstream_id: workstream.id, from: 'chip'});
    window.open(roadmap.open_url, '_blank', 'noopener,noreferrer');
  };
  const embed =
    roadmap && shouldShowRoadmapEmbed ? (
      <WorkstreamRoadmapEmbed
        data={roadmap}
        disabled={remove.isPending}
        onOpen={openRoadmap}
        onEdit={() => setIsFormOpen(true)}
        onRemove={roadmap.id ? () => remove.mutate(roadmap.id!) : undefined}
      />
    ) : null;

  return (
    <>
      <span ref={anchorRef} className={atoms({display: 'inline-flex'})}>
        {roadmap && !isWorkstreamProductRoadmapIframeEnabled ? (
          <WorkstreamRoadmapMenu
            disabled={remove.isPending}
            onOpen={openRoadmap}
            onEdit={() => setIsFormOpen(true)}
            onRemove={roadmap.id ? () => remove.mutate(roadmap.id!) : undefined}
          />
        ) : (
          <WorkstreamRoadmapChip
            showAddIcon={roadmap === null}
            withDropdownIcon={Boolean(roadmap)}
            isSelected={shouldShowRoadmapEmbed}
            isExpanded={shouldShowRoadmapEmbed}
            // Enabled while the lookup runs; the plus shows once there's no roadmap.
            disabled={isError}
            aria-expanded={
              roadmap ? shouldShowRoadmapEmbed : roadmap === null ? isFormOpen : undefined
            }
            onClick={() => {
              if (roadmap === null) setIsFormOpen(true);
              else if (roadmap) {
                const expanded = !isEmbedOpen;
                logWorkstreamsInteraction('roadmap_embed_toggled', {
                  workstream_id: workstream.id,
                  expanded,
                });
                setIsEmbedOpen(expanded);
              }
            }}
          />
        )}
      </span>
      {embedSlot ? createPortal(embed, embedSlot) : embed}
      <Popover
        open={isFormOpen}
        anchorEl={anchorRef.current}
        onToggle={setIsFormOpen}
        // The link field takes focus itself (autoFocus), ready for a paste.
        shouldFocusContentOnOpen={false}
        placement="bottom-start"
        offsetDistance={8}
      >
        <Popover.Overlay>
          <Popover.Content>
            <Popover.Body>
              {isFormOpen && (
                <RoadmapLinkForm
                  workstream={workstream}
                  roadmap={roadmap ?? null}
                  onDone={() => setIsFormOpen(false)}
                />
              )}
            </Popover.Body>
          </Popover.Content>
        </Popover.Overlay>
      </Popover>
      {remove.isError && (
        <WorkstreamModalFieldError>{saveErrorMessage(remove.error)}</WorkstreamModalFieldError>
      )}
    </>
  );
};
